import { useState } from 'react';
import { api } from '../api.ts';
import { Logo, Skyline } from '../art/Scenes.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { CLASS_INFO } from '../state.tsx';
import { SpeakerButton } from './Options.tsx';

export function AuthPage({ content, onLoggedIn, resetToken }: { content: any; onLoggedIn: () => void; resetToken?: string }) {
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState({ username: '', password: '' });
  const [reg, setReg] = useState({ username: '', password: '', characterName: '', charClass: 'maniac', registrationCode: '' });
  const [portrait, setPortrait] = useState(() => Math.floor(Math.random() * 1_000_000));
  const [newPass, setNewPass] = useState('');
  const [notes, setNotes] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="game">
        <div className="header">
          <Skyline />
          <Logo name={content.gameName} />
        </div>
        <div className="nav"><a className="active">Welcome to the family</a><span className="grow" /><SpeakerButton /></div>
        <div className="content">
          <div className="login-news">
            <span><b>{content.changelog?.[0] ? `New in v${content.changelog[0].version}: ${content.changelog[0].title}` : 'Patch notes'}</b></span>
            <a onClick={() => setNotes(true)}>Read the patch notes →</a>
          </div>
          {err && <div className="flash bad"><div>{err}</div></div>}
          {msg && <div className="flash good"><div>{msg}</div></div>}
          {resetToken ? (
            <div className="panel">
              <div className="panel-title">Choose a new password</div>
              <div className="panel-body">
                <form onSubmit={(e) => { e.preventDefault(); run(async () => { await api.post('/api/auth/reset-password', { token: resetToken, password: newPass }); setMsg('Password changed. You can log in now.'); window.location.hash = '#/home'; }); }}>
                  <div className="form-row"><label>New password (8+ characters)</label><input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} /></div>
                  <button className="btn" disabled={busy}>Save password</button>
                </form>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="panel grow">
                <div className="panel-title">Already a made man? Log in</div>
                <div className="panel-body">
                  <form onSubmit={(e) => { e.preventDefault(); run(async () => { await api.post('/api/auth/login', login); onLoggedIn(); }); }}>
                    <div className="form-row"><label>Username</label><input autoComplete="username" value={login.username} onChange={(e) => setLogin({ ...login, username: e.target.value })} /></div>
                    <div className="form-row"><label>Password</label><input type="password" autoComplete="current-password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} /></div>
                    <button className="btn big" disabled={busy}>Log in</button>
                  </form>
                  <p className="muted small" style={{ marginTop: 14 }}>Forgot your password? Ask the game's administrator for a reset link.</p>
                </div>
              </div>
              <div className="panel grow" style={{ flex: 1.4 }}>
                <div className="panel-title">New in town? Start your family</div>
                <div className="panel-body">
                  <form onSubmit={(e) => { e.preventDefault(); run(async () => { await api.post('/api/auth/register', { ...reg, portrait }); onLoggedIn(); }); }}>
                    <div className="row">
                      <div className="grow">
                        <div className="form-row"><label>Username (for logging in)</label><input autoComplete="username" value={reg.username} onChange={(e) => setReg({ ...reg, username: e.target.value })} /></div>
                        <div className="form-row"><label>Password (8+ characters)</label><input type="password" autoComplete="new-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} /></div>
                        <div className="form-row"><label>Character name (what others see)</label><input value={reg.characterName} onChange={(e) => setReg({ ...reg, characterName: e.target.value })} placeholder='e.g. Tony "Two Times" Russo' /></div>
                      </div>
                      <div className="center">
                        <Portrait seed={portrait} size={96} />
                        <div><a className="small" onClick={() => setPortrait(Math.floor(Math.random() * 1_000_000))}>New face ↻</a></div>
                      </div>
                    </div>
                    <div className="form-row">
                      <label>Choose your class</label>
                      <div className="class-pick">
                        {Object.entries(CLASS_INFO).map(([id, c]) => (
                          <label key={id} className={reg.charClass === id ? 'on' : ''}>
                            <input type="radio" name="cls" checked={reg.charClass === id} onChange={() => setReg({ ...reg, charClass: id })} />
                            <b className="gold">{c.name}</b>
                            <div className="small">{c.perk}</div>
                          </label>
                        ))}
                      </div>
                    </div>
                    {content.rules.registrationCodeRequired && (
                      <div className="form-row"><label>Invitation code</label><input value={reg.registrationCode} onChange={(e) => setReg({ ...reg, registrationCode: e.target.value })} /></div>
                    )}
                    <button className="btn big" disabled={busy}>Join the Mafia</button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="footer">
          {content.gameName}{content.version && <> v{content.version}</>} · <a className="patch-notes-link" onClick={() => setNotes(true)}>Patch notes</a> · a fan-made tribute to the classic browser mafia games of 2008–2011
        </div>
      </div>
      {notes && <PatchNotes changelog={content.changelog ?? []} onClose={() => setNotes(false)} />}
    </div>
  );
}

/** Every version's patch notes, newest first (opened from the login screen). */
function PatchNotes({ changelog, onClose }: { changelog: any[]; onClose: () => void }) {
  const [open, setOpen] = useState<string>(changelog[0]?.version ?? '');
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Patch notes">
        <div className="modal-title">Patch notes</div>
        <div className="modal-body patch-notes">
          {changelog.map((c) => (
            <div key={c.version} className="patch-entry">
              <a className="patch-head" onClick={() => setOpen(open === c.version ? '' : c.version)}>
                <span>{open === c.version ? '▾' : '▸'} <b>v{c.version}</b> · {c.title}</span><span className="dim small">{c.date}</span>
              </a>
              {open === c.version && <ul>{c.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}</ul>}
            </div>
          ))}
          <div className="right"><button className="btn big" onClick={onClose}>Close</button></div>
        </div>
      </div>
    </div>
  );
}
